import PropTypes from 'prop-types';

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

const HEADER_TEAL = '#55BEC3';
const HEADER_TEAL_DARK = '#35AEB5';
const HEADER_TEXT = '#FFFFFF';
const TABLE_HEADER_BG = '#F7F9FB';
const TABLE_TEXT = '#43516A';
const BORDER_COLOR = '#E0E5ED';

// Preserve black, bold status text.
const getStatusColor = () => '#000000';

// Preserve the calendar date from ISO strings without converting
// midnight UTC into the previous day in Pacific time.
const formatDate = (date) => {
    if (!date) return '-';

    if (date instanceof Date) {
        return Number.isNaN(date.getTime())
            ? '-'
            : date.toISOString().slice(0, 10);
    }

    const value = String(date).trim();
    const isoDateMatch = value.match(/^(\d{4}-\d{2}-\d{2})(?:$|T|\s)/);

    if (isoDateMatch) {
        return isoDateMatch[1];
    }

    const parsedDate = new Date(value);

    return Number.isNaN(parsedDate.getTime())
        ? value
        : parsedDate.toISOString().slice(0, 10);
};

const formatStatus = (status) => {
    if (!status) return 'Unknown';

    return String(status)
        .trim()
        .toLowerCase()
        .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};

const TaskCompletionModal = ({
    open,
    onClose,
    taskId,
    jobNumber,
    taskNumber,
    canComplete = false,
    unresolved = [],
    loading = false,
    error = '',
    onComplete,
    completing = false,
    title = 'Task Completion Validation',
}) => {
    const handleComplete = () => {
        if (canComplete && onComplete) {
            onComplete(taskId);
        }
    };

    const items = Array.isArray(unresolved) ? unresolved : [];

    return (
        <Dialog
            open={open}
            onClose={completing ? undefined : onClose}
            fullWidth
            maxWidth="lg"
            aria-labelledby="task-completion-dialog-title"
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 12px 36px rgba(20, 40, 60, 0.18)',
                    backgroundColor: '#FFFFFF',
                },
            }}
        >
            {/* Teal header matching the Daily Work Reports modal */}

            <DialogTitle
                id="task-completion-dialog-title"
                sx={{
                    position: 'relative',
                    px: 2.5,
                    py: 1.75,
                    pr: 7,
                    color: '#FFFFFF',
                    background: `linear-gradient(110deg, ${HEADER_TEAL} 0%, ${HEADER_TEAL_DARK} 100%)`,
                }}
            >
                <Typography
                    component="div"
                    sx={{
                        color: '#FFFFFF',
                        fontSize: 16,
                        lineHeight: 1.5,
                        fontWeight: 700,
                    }}
                >
                    {title}
                </Typography>

                <Typography
                    component="div"
                    sx={{
                        mt: 0.25,
                        color: '#FFFFFF',
                        fontSize: 12,
                        fontWeight: 400,
                    }}
                >
                    Job {jobNumber || '-'} • Task {taskNumber || taskId || '-'}
                </Typography>

                <IconButton
                    aria-label="Close dialog"
                    onClick={onClose}
                    disabled={completing}
                    size="small"
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#FFFFFF',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            color: '#FFFFFF',
                            backgroundColor: 'rgba(255,255,255,0.15)',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>


            <DialogContent
                sx={{
                    p: 2.5,
                    backgroundColor: '#FFFFFF',
                    '&:first-of-type': {
                        pt: 2.5,
                    },
                }}
            >
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 180,
                        }}
                    >
                        <CircularProgress sx={{ color: HEADER_TEAL_DARK }} />
                    </Box>
                ) : (
                    <>
                        {error && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {canComplete && !error ? (
                            <Alert severity="success" sx={{ mb: 1 }}>
                                All Schedules and Todo Drafts are resolved. This task
                                is ready to be completed.
                            </Alert>
                        ) : (
                            <>
                                {!error && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        This task cannot be completed until all outstanding Schedule and Todo Draft items have been resolved.
                                    </Alert>
                                )}

                                <TableContainer
                                    component={Paper}
                                    variant="outlined"
                                    sx={{
                                        border: `1px solid ${BORDER_COLOR}`,
                                        borderRadius: '10px',
                                        boxShadow: 'none',
                                        overflowX: 'auto',
                                    }}
                                >
                                    <Table
                                        size="small"
                                        aria-label="Unresolved task items"

                                        sx={{
                                            minWidth: 700,
                                            borderCollapse: 'separate',
                                            borderSpacing: 0,

                                            '& .MuiTableCell-root': {
                                                borderBottom: '1px solid #E6EAF0',
                                                px: 1.75,
                                                py: 1.5,
                                                fontSize: 12.5,
                                                color: TABLE_TEXT,
                                            },

                                            '& .MuiTableHead-root .MuiTableCell-root': {
                                                backgroundColor: '#F7F9FB',
                                                color: '#526078',
                                                fontSize: 11.5,
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                                borderBottom: '1px solid #E6EAF0',
                                            },

                                            '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                                                borderBottom: '1px solid #E6EAF0',
                                            },

                                            '& .MuiTableBody-root .MuiTableRow-root:hover': {
                                                backgroundColor: '#F8FBFC',
                                            },
                                        }}

                                    >
                                        <TableHead>
                                            <TableRow
                                                sx={{
                                                    backgroundColor: TABLE_HEADER_BG,
                                                    '& .MuiTableCell-root': {
                                                        color: '#526078',
                                                        fontSize: 11.5,
                                                        fontWeight: 700,
                                                        whiteSpace: 'nowrap',
                                                        py: 1.5,
                                                    },
                                                }}
                                            >
                                                <TableCell>Type</TableCell>
                                                <TableCell>Requested Date</TableCell>
                                                <TableCell>Assignee</TableCell>
                                                <TableCell>Checked Manager</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>


                                        <TableBody
                                            sx={{
                                                '& .MuiTableCell-root': {
                                                    borderBottom: '1px solid #D5E0E0 !important',
                                                    boxShadow: 'inset 0 -1px 0 #D5E0E0',
                                                },
                                                '& .MuiTableRow-root:hover': {
                                                    backgroundColor: '#D5EEEE',
                                                },
                                            }}
                                        >
                                            {items.length > 0 ? (
                                                items.map((item, index) => (
                                                    <TableRow
                                                        key={item.id || index}
                                                        sx={{
                                                            '& > .MuiTableCell-root': {
                                                                borderBottom: '1px solid #555758 !important',
                                                            },
                                                            '&:hover': {
                                                                backgroundColor: '#F8FBFC',
                                                            },
                                                        }}
                                                    >
                                                        <TableCell>
                                                            {item.scheduling || item.type || '-'}
                                                        </TableCell>

                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            {formatDate(item.field_requested_date)}
                                                        </TableCell>

                                                        <TableCell>
                                                            {item.assignee || '-'}
                                                        </TableCell>

                                                        <TableCell>
                                                            {item.checked_manager || '-'}
                                                        </TableCell>

                                                        <TableCell>
                                                            {formatStatus(item.status)}
                                                        </TableCell>

                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={5}
                                                        align="center"
                                                        sx={{ py: 4, borderBottom: 'none !important' }}
                                                    >
                                                        No unresolved records were returned.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>

                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 2.5,
                    py: 2,
                    gap: 1,
                    borderTop: `1px solid ${BORDER_COLOR}`,
                    backgroundColor: '#FFFFFF',
                }}
            >

                <Button
                    onClick={onClose}
                    disabled={completing}
                    variant="outlined"
                    sx={{
                        color: HEADER_TEAL_DARK,
                        borderColor: HEADER_TEAL_DARK,
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '7px',
                        px: 2.5,
                        '&:hover': {
                            color: '#FFFFFF',
                            backgroundColor: HEADER_TEAL_DARK,
                            borderColor: HEADER_TEAL_DARK,
                        },
                        '&.Mui-disabled': {
                            color: HEADER_TEAL_DARK,
                            borderColor: BORDER_COLOR,
                            opacity: 0.5,
                        },
                    }}
                >
                    Close
                </Button>


                {canComplete && onComplete && !loading && !error && (
                    <Button
                        variant="contained"
                        onClick={handleComplete}
                        disabled={completing}
                        sx={{
                            background: `linear-gradient(110deg, ${HEADER_TEAL} 0%, ${HEADER_TEAL_DARK} 100%)`,
                            color: '#FFFFFF',
                            fontWeight: 700,
                            textTransform: 'none',
                            borderRadius: '7px',
                            px: 2.5,
                            boxShadow: 'none',
                            '&:hover': {
                                background: HEADER_TEAL_DARK,
                                boxShadow: 'none',
                            },
                        }}
                    >
                        {completing ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Complete Task'
                        )}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

TaskCompletionModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    taskId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    canComplete: PropTypes.bool,
    unresolved: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            type: PropTypes.string,
            scheduling: PropTypes.string,
            field_requested_date: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.instanceOf(Date),
            ]),
            assignee: PropTypes.string,
            checked_manager: PropTypes.string,
            status: PropTypes.string,
        })
    ),
    loading: PropTypes.bool,
    error: PropTypes.string,
    onComplete: PropTypes.func,
    completing: PropTypes.bool,
    title: PropTypes.string,
    jobNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    taskNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
};

export default TaskCompletionModal;